/**
 * PatternIQ Admin - API Keys Management
 * =======================================
 * Phase C4: External Agent API
 * 
 * UI for managing external API keys:
 * - View existing keys (masked)
 * - Create new keys
 * - Revoke keys
 * - View usage statistics
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Key, 
  Plus, 
  Copy, 
  Trash2, 
  CheckCircle, 
  AlertCircle,
  Clock,
  Activity,
  Shield,
  Eye,
  EyeOff,
  RefreshCw,
  ExternalLink,
  Bot
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Slider } from '../../components/ui/slider';

interface ApiKey {
  id: string;
  name: string;
  allowedAgents: string[];
  rateLimitPerMinute: number;
  createdAt: string;
  revokedAt?: string;
  lastUsedAt?: string;
  usageCount: number;
  status: 'active' | 'revoked';
}

interface ApiKeysResponse {
  success: boolean;
  keys: ApiKey[];
  count: number;
}

interface CreateKeyResponse {
  success: boolean;
  message: string;
  key: {
    id: string;
    name: string;
    apiKey: string;
    allowedAgents: string[];
    rateLimitPerMinute: number;
    createdAt: string;
  };
}

// Available agents for restriction
const AVAILABLE_AGENTS = [
  { id: 'ulta_merchandising', name: 'Ulta Merchandising' },
  { id: 'macys_performance', name: "Macy's Performance" },
  { id: 'jcp_analyst', name: 'JCP Analyst' },
  { id: 'inventory_risk_replenishment', name: 'Inventory Risk' },
  { id: 'promo_pricing', name: 'Promo & Pricing' },
  { id: 'forecast_explainer', name: 'Forecast Explainer' },
  { id: 'data_quality_guardian', name: 'Data Quality' },
  { id: 'executive_summary', name: 'Executive Summary' }
];

export default function ApiKeys() {
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState<CreateKeyResponse['key'] | null>(null);
  const [showNewKey, setShowNewKey] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Form state for new key
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyAgents, setNewKeyAgents] = useState<string[]>([]);
  const [newKeyRateLimit, setNewKeyRateLimit] = useState(60);
  
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Fetch API keys
  const { data, isLoading, error, refetch } = useQuery<ApiKeysResponse>({
    queryKey: ['api-keys'],
    queryFn: async () => {
      const res = await fetch('/api/external/keys');
      if (!res.ok) throw new Error('Failed to fetch API keys');
      return res.json();
    }
  });

  // Create key mutation
  const createMutation = useMutation({
    mutationFn: async (request: { name: string; allowedAgents: string[]; rateLimitPerMinute: number }) => {
      const res = await fetch('/api/external/keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to create API key');
      }
      return res.json() as Promise<CreateKeyResponse>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setNewKeyResult(data.key);
      setShowNewKey(true);
      setNewKeyName('');
      setNewKeyAgents([]);
      setNewKeyRateLimit(60);
    },
    onError: (error: Error) => {
      setStatusMessage({ type: 'error', message: error.message });
      setTimeout(() => setStatusMessage(null), 5000);
    }
  });

  // Revoke key mutation
  const revokeMutation = useMutation({
    mutationFn: async (keyId: string) => {
      const res = await fetch(`/api/external/keys?keyId=${keyId}`, {
        method: 'DELETE'
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || 'Failed to revoke API key');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setStatusMessage({ type: 'success', message: 'API key revoked successfully' });
      setTimeout(() => setStatusMessage(null), 3000);
    },
    onError: (error: Error) => {
      setStatusMessage({ type: 'error', message: error.message });
      setTimeout(() => setStatusMessage(null), 5000);
    }
  });

  const handleCreateKey = () => {
    if (!newKeyName.trim()) {
      setStatusMessage({ type: 'error', message: 'Please enter a key name' });
      return;
    }
    createMutation.mutate({
      name: newKeyName.trim(),
      allowedAgents: newKeyAgents,
      rateLimitPerMinute: newKeyRateLimit
    });
  };

  const handleCopyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCloseNewKeyDialog = () => {
    setNewKeyResult(null);
    setShowNewKey(false);
    setIsCreateDialogOpen(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Failed to load API keys</p>
        </div>
      </div>
    );
  }

  const activeKeys = data?.keys.filter(k => k.status === 'active') || [];
  const revokedKeys = data?.keys.filter(k => k.status === 'revoked') || [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Key className="w-7 h-7 text-amber-600" />
            External API Keys
          </h1>
          <p className="text-gray-600 mt-1">
            Manage API keys for external systems to access PatternIQ agents
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create API Key
          </Button>
        </div>
      </div>

      {/* Status Toast */}
      {statusMessage && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-2 ${
          statusMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {statusMessage.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          {statusMessage.message}
        </div>
      )}

      {/* Active Keys */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-600" />
            Active API Keys ({activeKeys.length})
          </CardTitle>
          <CardDescription>
            Keys that can be used to access the External Agent API
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeKeys.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Key className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No active API keys</p>
              <p className="text-sm mt-2">Create a key to enable external API access</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeKeys.map((key) => (
                <div key={key.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{key.name}</h3>
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Created: {formatDate(key.createdAt)}
                      </span>
                      {key.lastUsedAt && (
                        <span className="flex items-center gap-1">
                          <Activity className="w-4 h-4" />
                          Last used: {formatDate(key.lastUsedAt)}
                        </span>
                      )}
                      <span>
                        Usage: {key.usageCount} requests
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-gray-500">Rate limit: {key.rateLimitPerMinute}/min</span>
                      {key.allowedAgents.length > 0 ? (
                        <span className="text-xs text-gray-500">
                          Agents: {key.allowedAgents.length} selected
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">All agents</span>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-600 hover:text-red-700"
                    onClick={() => {
                      if (confirm(`Revoke API key "${key.name}"? This cannot be undone.`)) {
                        revokeMutation.mutate(key.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revoked Keys */}
      {revokedKeys.length > 0 && (
        <Card className="opacity-75">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-500">
              <Key className="w-5 h-5" />
              Revoked Keys ({revokedKeys.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {revokedKeys.map((key) => (
                <div key={key.id} className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
                  <div>
                    <span className="font-medium text-gray-600">{key.name}</span>
                    <Badge variant="secondary" className="ml-2">Revoked</Badge>
                  </div>
                  <span className="text-sm text-gray-500">
                    Revoked: {key.revokedAt ? formatDate(key.revokedAt) : 'Unknown'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* API Documentation Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <ExternalLink className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900 mb-2">Using the External Agent API</h3>
              <div className="text-sm text-amber-800 space-y-2">
                <p><strong>Endpoint:</strong> POST /api/external/agent</p>
                <p><strong>Authentication:</strong> Bearer token (your API key)</p>
                <pre className="bg-amber-100 p-3 rounded-md overflow-x-auto text-xs mt-2">
{`curl -X POST https://patterniqai.com/api/external/agent \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "agentId": "ulta_merchandising",
    "message": "What are my top selling SKUs?",
    "context": {
      "retailerId": "10000000-0000-0000-0000-000000000001"
    }
  }'`}
                </pre>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Key Dialog */}
      <Dialog open={isCreateDialogOpen && !newKeyResult} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API Key</DialogTitle>
            <DialogDescription>
              Create a new API key for external system access
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Key Name</Label>
              <Input
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g., Production Integration"
              />
              <p className="text-xs text-gray-500">A descriptive name to identify this key</p>
            </div>

            <div className="space-y-2">
              <Label>Rate Limit: {newKeyRateLimit} requests/minute</Label>
              <Slider
                value={[newKeyRateLimit]}
                onValueChange={(value) => setNewKeyRateLimit(value[0])}
                min={10}
                max={300}
                step={10}
              />
            </div>

            <div className="space-y-2">
              <Label>Allowed Agents (leave empty for all)</Label>
              <div className="grid grid-cols-2 gap-2 p-3 border rounded-md bg-gray-50 max-h-48 overflow-y-auto">
                {AVAILABLE_AGENTS.map(agent => (
                  <div key={agent.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`agent-${agent.id}`}
                      checked={newKeyAgents.includes(agent.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setNewKeyAgents([...newKeyAgents, agent.id]);
                        } else {
                          setNewKeyAgents(newKeyAgents.filter(a => a !== agent.id));
                        }
                      }}
                    />
                    <label htmlFor={`agent-${agent.id}`} className="text-sm cursor-pointer">
                      {agent.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateKey} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Key Result Dialog */}
      <Dialog open={!!newKeyResult} onOpenChange={handleCloseNewKeyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              API Key Created
            </DialogTitle>
            <DialogDescription>
              <strong className="text-red-600">Save this key now!</strong> It will not be shown again.
            </DialogDescription>
          </DialogHeader>

          {newKeyResult && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Key Name</Label>
                <p className="font-medium">{newKeyResult.name}</p>
              </div>

              <div className="space-y-2">
                <Label>API Key</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 font-mono text-sm p-3 bg-gray-100 rounded-md break-all">
                    {showNewKey ? newKeyResult.apiKey : '•'.repeat(40)}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowNewKey(!showNewKey)}
                  >
                    {showNewKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleCopyKey(newKeyResult.apiKey)}
                  >
                    {copySuccess ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Important:</strong> Store this key securely. You won't be able to see it again.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={handleCloseNewKeyDialog}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

